import { Base } from '@studiometa/js-toolkit';
import mapboxgl from 'mapbox-gl';
import MapboxMarker from './MapboxMarker.js';
import MapboxCluster from './MapboxCluster.js';

/**
 * Contrôle Mapbox personnalisé « recentrer sur ma position » (maquette,
 * point 6, section « Rendu visuel » : contrôles à droite — recentrer, +, –).
 * Réutilise le bouton « Me géolocaliser » du bandeau (`#store-locator-geolocate`,
 * logique du point 5 dans `MapboxCluster.bindGeolocateButton()`/
 * `requestPosition()`) plutôt que de dupliquer la géolocalisation ici : un
 * simple `click()` programmatique sur ce bouton. N'est ajouté que si ce
 * bouton existe dans la page (voir `mounted()` ci-dessous) — jamais sur une
 * page « ville » (invariant R8), qui ne le rend pas.
 */
class RecenterControl {
  onAdd() {
    this.container = document.createElement('div');
    this.container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group sl-map-recenter';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'sl-map-recenter__btn';
    button.setAttribute('aria-label', 'Me géolocaliser');
    button.title = 'Me géolocaliser';
    button.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/><path d="M12 2V5M12 19V22M22 12H19M5 12H2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
    button.addEventListener('click', () => {
      document.getElementById('store-locator-geolocate')?.click();
    });
    this.container.appendChild(button);
    return this.container;
  }

  onRemove() {
    this.container.parentNode?.removeChild(this.container);
  }
}

/**
 * mapboxComponentResolver
 * @param {*} Component
 * @returns {Class}
 */
function mapboxComponentResolver(Component) {
  return (mapboxMap) => {
    return new Promise((resolve) => {
      if (mapboxMap.isLoaded) {
        resolve(Component);
      } else {
        mapboxMap.$on('map-load', () => resolve(Component));
      }
    });
  };
}

/**
 *
 */
export default class MapboxMap extends Base {
  static config = {
    name: 'MapboxMap',
    emits: ['map-load'],
    options: {
      accessToken: String,
      zoom: Number,
      center: { type: Array, default: () => [0, 0] },
    },
    components: {
      MapboxMarker: mapboxComponentResolver(MapboxMarker),
      MapboxCluster: mapboxComponentResolver(MapboxCluster),
    },
  };

  mapbox;

  map;

  isLoaded = false;

  /**
   * mapboxOptions
   * @returns {object}
   */
  get mapboxOptions() {
    return {
      container: this.$el,
      center: this.$options.center,
      accessToken: this.$options.accessToken,
      zoom: this.$options.zoom,
      projection: 'mercator',
      pitch: 0,
      style: 'mapbox://styles/nielsendesignfr/cmmd92han00aq01scehkd2jid',
    };
  }

  /**
   * mounted
   *
   * Molette de la souris (chantier store-locator-molette-recherche, 08/10) :
   * sur ordinateur (pointeur fin : souris, pavé tactile), la molette SEULE
   * fait défiler la page, y compris quand le pointeur survole la carte ; le
   * zoom à la molette demande Ctrl (⌘ sur Mac) — le pincement d'un pavé
   * tactile arrive lui aussi comme une molette avec Ctrl et zoome donc la
   * carte. Un message d'aide s'affiche brièvement sur la carte (voir
   * `bindWheelGuard()`). Le zoom à la molette de Mapbox (`scrollZoom`) reste
   * activé : la garde ci-dessous ne le laisse recevoir que les molettes avec
   * Ctrl/⌘. L'option Mapbox `cooperativeGestures` n'est volontairement PAS
   * utilisée : elle impose aussi « deux doigts pour déplacer » au tactile,
   * alors que mobile et tablette doivent garder leurs gestes (un doigt en
   * plein écran mobile, pincement). Écran tactile sans souris : aucun
   * évènement molette, donc rien ne change.
   */
  async mounted() {
    this.mapbox = mapboxgl;
    this.mapbox.accessToken =
      'pk.eyJ1IjoibmllbHNlbmRlc2lnbmZyIiwiYSI6ImNtaGFjcmdkZDFlajkyc3NicmU0d3o0bG0ifQ.SXT2rjKfkzd0LaX3Ojbg8g';
    this.map = new this.mapbox.Map(this.mapboxOptions);
    this.map.on('load', () => {
      this.isLoaded = true;
      this.$emit('map-load');
    });
    this.bindWheelGuard();

    // Contrôles à droite (chantier store-locator-revendeurs, point 6, section
    // « Rendu visuel » : « contrôles à droite » de la maquette — recentrer,
    // +, –, contre le seul zoom à gauche auparavant).
    if (document.getElementById('store-locator-geolocate')) {
      this.map.addControl(new RecenterControl(), 'top-right');
    }
    const nav = new mapboxgl.NavigationControl({
      showCompass: false,
    });
    this.map.addControl(nav, 'top-right');
  }

  /**
   * Garde de la molette (voir `mounted()`). Écouteur posé en phase de
   * capture sur le conteneur de la carte : une molette SANS Ctrl/⌘, sur un
   * ordinateur, est arrêtée avant d'atteindre le zoom Mapbox (qui l'aurait
   * « avalée » avec `preventDefault()`) ; le navigateur la traite alors
   * normalement et fait défiler la page. Une molette avec Ctrl/⌘ n'est pas
   * touchée : Mapbox zoome et empêche le zoom de la page du navigateur.
   * Écouteur passif : il n'appelle jamais `preventDefault()`, il ne bloque
   * donc ni le défilement ni les clics. N'agit que sur la zone du dessin de
   * la carte (pas sur les boutons, les bulles ouvertes) et seulement si un
   * pointeur fin est disponible — sur un téléphone ou une tablette tactile
   * sans souris, la carte garde son comportement d'origine. Carte en plein
   * écran (`.map-pane.is-fullscreen`, aussi atteignable sur ordinateur dans
   * une fenêtre étroite) : la garde ne s'applique pas, la molette zoome
   * comme avant et ne fait pas défiler la page cachée derrière.
   */
  bindWheelGuard() {
    this.wheelHintTimer = null;
    this.wheelHintRemoveTimer = null;
    this.wheelHintBox = null;
    this.wheelHintLive = document.createElement('div');
    this.wheelHintLive.className = 'sl-map-hint';
    this.wheelHintLive.setAttribute('role', 'status');
    this.wheelHintLive.setAttribute('aria-live', 'polite');
    this.wheelHintLive.style.cssText =
      'position:absolute;inset:0;z-index:3;display:flex;align-items:center;justify-content:center;pointer-events:none;';
    this.$el.appendChild(this.wheelHintLive);

    this.wheelGuard = (event) => {
      if (event.ctrlKey || event.metaKey) return;
      if (!window.matchMedia('(any-pointer: fine)').matches) return;
      // Carte plein écran (fenêtre étroite, < 861 px) : la carte est seule à
      // l'écran et recouvre la page, rien à faire défiler derrière elle ; la
      // molette zoome comme avant, sans message (revue finale du 08/10, R1).
      if (this.$el.closest('.map-pane.is-fullscreen')) return;
      const canvasContainer = this.map?.getCanvasContainer();
      if (!canvasContainer || !canvasContainer.contains(event.target)) return;
      event.stopPropagation();
      this.showWheelHint();
    };
    this.$el.addEventListener('wheel', this.wheelGuard, { capture: true, passive: true });
  }

  /**
   * Affiche le message d'aide de la molette (ou prolonge son affichage) puis
   * le retire tout seul. Région `aria-live="polite"` créée une fois pour
   * toutes, le texte n'y est inséré que pendant l'affichage.
   */
  showWheelHint() {
    clearTimeout(this.wheelHintTimer);
    clearTimeout(this.wheelHintRemoveTimer);
    if (!this.wheelHintBox) {
      const box = document.createElement('div');
      box.className = 'sl-map-hint__message';
      box.textContent = /Mac|iPad/i.test(navigator.userAgent)
        ? 'Utilisez ⌘ + molette pour zoomer'
        : 'Utilisez Ctrl + molette pour zoomer';
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      box.style.cssText =
        "max-width:80%;padding:10px 16px;border-radius:6px;background:rgba(10,10,18,0.82);color:#fff;font-family:'Montserrat',sans-serif;font-size:0.8125rem;font-weight:500;line-height:1.35;text-align:center;opacity:0;" +
        (reduceMotion ? '' : 'transition:opacity 0.2s ease-out;');
      this.wheelHintLive.appendChild(box);
      this.wheelHintBox = box;
      // Deux images : l'opacité de départ doit être peinte pour que le fondu ait lieu.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (this.wheelHintBox === box) box.style.opacity = '1';
      }));
    }
    this.wheelHintTimer = setTimeout(() => this.hideWheelHint(), 1400);
  }

  /**
   * Retire le message d'aide (fondu puis suppression).
   */
  hideWheelHint() {
    const box = this.wheelHintBox;
    if (!box) return;
    this.wheelHintBox = null;
    box.style.opacity = '0';
    this.wheelHintRemoveTimer = setTimeout(() => box.remove(), 250);
  }

  /**
   * reset
   */
  async onWindowMapreset() {
    this.map.flyTo({ center: this.$options.center, zoom: this.$options.zoom });
  }

  /**
   * Carte plein écran mobile (chantier store-locator-revendeurs, point 6,
   * scénario 37) : `MobileMapPeek` redimensionne `.map-pane` par CSS
   * (position fixed) sans jamais recréer l'instance Mapbox ; ce composant ne
   * connaît pas ce changement de taille tant qu'on ne le lui dit pas —
   * `map.resize()` est nécessaire pour que le rendu WebGL suive le nouveau
   * conteneur (sinon la carte reste coupée ou mal centrée après le
   * changement de mise en page). Délai court : laisse la transition CSS
   * (taille) s'appliquer avant de mesurer le conteneur.
   * @param {CustomEvent} event
   */
  onWindowStoreLocatorMapFullscreen() {
    setTimeout(() => this.map?.resize(), 50);
  }

  /**
   * destroyed
   */
  destroyed() {
    clearTimeout(this.wheelHintTimer);
    clearTimeout(this.wheelHintRemoveTimer);
    this.$el.removeEventListener('wheel', this.wheelGuard, { capture: true });
    this.wheelHintLive?.remove();
    this.map?.remove();
  }
}
